import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthService from '../services/AuthService';
import {
  clearSessionTimeoutPending,
  getSessionTimeoutReason,
  isSessionTimeoutPending,
  subscribeSessionTimeout,
} from '../services/sessionTimeoutStore';

/** Match JWT access token lifetime (15 minutes). */
const SESSION_MS = 15 * 60 * 1000;
const TOKEN_CHECK_MS = 30 * 1000;

const PUBLIC_PREFIXES = ['/login', '/verify-email', '/home', '/', '/contact-sales', '/about', '/privacy', '/lead-form', '/partner/join', '/subscription-inactive', '/meta-oauth-callback'];

function isPublicPath(pathname) {
  if (!pathname) return true;
  if (pathname === '/') return true;
  return PUBLIC_PREFIXES.some((p) => p !== '/' && (pathname === p || pathname.startsWith(`${p}/`)));
}

function hasActiveSession() {
  return Boolean(AuthService.getToken()) || isSessionTimeoutPending();
}

export default function IdleSessionWarning() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [timeoutReason, setTimeoutReason] = useState('idle');
  const [extending, setExtending] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const warnedRef = useRef(false);

  const openWarning = useCallback((reason) => {
    warnedRef.current = true;
    setTimeoutReason(reason);
    setShowWarning(true);
  }, []);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    warnedRef.current = false;
    clearSessionTimeoutPending();
    setShowWarning(false);
    setTimeoutReason('idle');
  }, []);

  const stayLoggedIn = async () => {
    setExtending(true);
    try {
      await AuthService.extendSession();
      resetActivity();
    } catch {
      setShowWarning(false);
      AuthService.performLogout(navigate);
    } finally {
      setExtending(false);
    }
  };

  const handleLogout = () => {
    setShowWarning(false);
    clearSessionTimeoutPending();
    AuthService.performLogout(navigate);
  };

  useEffect(() => {
    if (isSessionTimeoutPending()) {
      openWarning(getSessionTimeoutReason());
    }
    return subscribeSessionTimeout(({ reason }) => {
      openWarning(reason);
    });
  }, [openWarning]);

  useEffect(() => {
    if (!hasActiveSession() || isPublicPath(location.pathname)) {
      if (!isSessionTimeoutPending()) {
        setShowWarning(false);
      }
      return undefined;
    }

    const onActivity = () => {
      if (!warnedRef.current) {
        lastActivityRef.current = Date.now();
      }
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));

    const idleTimer = window.setInterval(() => {
      if (warnedRef.current) return;
      const idleFor = Date.now() - lastActivityRef.current;
      if (idleFor >= SESSION_MS) {
        AuthService.notifySessionTimeout('idle');
      }
    }, 5000);

    const tokenTimer = window.setInterval(() => {
      if (warnedRef.current) return;
      const token = AuthService.getToken();
      if (token && AuthService.isTokenExpired(token)) {
        AuthService.notifySessionTimeout('token-expired');
      }
    }, TOKEN_CHECK_MS);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      window.clearInterval(idleTimer);
      window.clearInterval(tokenTimer);
    };
  }, [location.pathname]);

  if (!showWarning) return null;

  const isTokenExpired = timeoutReason === 'token-expired';

  return (
    <div className="nf-idle-session-backdrop" role="presentation">
      <div className="nf-idle-session-modal" role="dialog" aria-modal="true" aria-labelledby="idle-session-title">
        <div className="nf-idle-session-icon">
          <i className="fas fa-hourglass-half" aria-hidden="true" />
        </div>
        <h2 id="idle-session-title" className="nf-idle-session-title">
          {isTokenExpired ? 'Session timed out' : 'Still there?'}
        </h2>
        <p className="nf-idle-session-desc">
          {isTokenExpired
            ? 'Your session has expired after 15 minutes. Stay logged in to refresh your session, or log out now.'
            : 'You have been inactive for 15 minutes. Choose stay logged in to continue your session or log out now.'}
        </p>
        <div className="nf-idle-session-actions">
          <button type="button" className="nf-btn nf-btn-secondary" onClick={handleLogout} disabled={extending}>
            Log out
          </button>
          <button type="button" className="nf-btn nf-btn-primary" onClick={stayLoggedIn} disabled={extending}>
            {extending ? 'Refreshing…' : 'Stay logged in'}
          </button>
        </div>
      </div>
    </div>
  );
}
